import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export const Privacy: React.FC = () => {
  return (
    <div className="legal-container">
      <nav className="legal-topnav">
        <div className="legal-topnav-brand">
          <div className="legal-topnav-logo">W</div>
          <span className="legal-topnav-name">Wealth Financial Systems</span>
        </div>
        <div className="legal-topnav-links">
          <Link to="/terms" className="legal-topnav-link">Terms of Service</Link>
          <Link to="/" className="legal-topnav-link">Home</Link>
        </div>
      </nav>

      <div className="legal-content">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: March 2, 2026</p>
        </div>

        <div className="legal-highlight">
          <h3>⚠️ Important Notice</h3>
          <p>
            Wealth Financial Systems is an <strong>educational and informational platform</strong>. We do not provide
            investment advice, manage accounts, or handle your financial assets. Our privacy practices
            are designed around protecting the account and usage data needed to deliver these educational tools.
          </p>
        </div>

        <div className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Welcome to Wealth Financial Systems ("we," "our," or "us"). We are committed to protecting your personal
            information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and
            safeguard your information when you use our platform, including the Wealth Overview dashboard, 4-Bucket
            System tools, FIRE Strategy calculator, Bucket Visualizer, IV Monitor, Trade Signals, and all related
            features (collectively, the "Service").
          </p>
          <p>
            By using the Service, you agree to the collection and use of information in accordance with this Privacy
            Policy. If you do not agree, please discontinue use of the Service.
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Information We Collect</h2>

          <h3>Information You Provide Directly</h3>
          <p>We collect the following personal information that you voluntarily provide to us:</p>
          <ul>
            <li><strong>Account Information:</strong> Email address and encrypted password used to create and authenticate your account</li>
            <li><strong>Profile &amp; Preferences:</strong> Subscription settings, alert preferences, notification configurations, and dashboard customizations</li>
            <li><strong>Financial Planning Inputs:</strong> Numbers and figures you enter into educational calculators (e.g., retirement age, savings goals, portfolio values). These inputs are used locally to generate outputs and are not stored on our servers unless you explicitly save them.</li>
            <li><strong>Support Communications:</strong> Any information you provide when contacting us for support, including the content of your messages</li>
            <li><strong>Payment Information:</strong> If you subscribe to a paid plan, billing details are processed by our third-party payment processor. We do not store full payment card numbers.</li>
          </ul>

          <h3>Information Collected Automatically</h3>
          <p>When you use our Service, we automatically collect certain technical data:</p>
          <ul>
            <li><strong>Usage Data:</strong> Pages visited, features accessed, time spent, clickstream data, and interactions with tools</li>
            <li><strong>Device &amp; Browser Information:</strong> IP address, browser type and version, operating system, device identifiers, screen resolution</li>
            <li><strong>Log Data:</strong> Access timestamps, API request logs, error logs, and session identifiers</li>
            <li><strong>Cookies &amp; Similar Technologies:</strong> Authentication tokens, session cookies, and preference cookies (see Section 9)</li>
          </ul>

          <h3>Market Data We Access on Your Behalf</h3>
          <p>
            To deliver market monitoring features, our servers retrieve publicly available market data (stock prices,
            options data, implied volatility, trade signals) from third-party financial data providers. This retrieval
            is performed server-side — we do not share your identity with these providers in connection with data requests.
          </p>
        </div>

        <div className="legal-section">
          <h2>3. How We Use Your Information</h2>

          <h3>Service Delivery</h3>
          <ul>
            <li>Create and maintain your user account</li>
            <li>Authenticate access to premium features based on your subscription tier</li>
            <li>Deliver IV monitoring alerts, trade signal notifications, and daily market reports</li>
            <li>Power educational tools including the 4-Bucket visualizer, FIRE calculator, and Lifecycle planner</li>
            <li>Generate AI-assisted market commentary and analysis (see Section 5)</li>
          </ul>

          <h3>Communications</h3>
          <ul>
            <li>Send alert emails and reports based on your active subscriptions (you control these in your profile)</li>
            <li>Respond to your support requests and inquiries</li>
            <li>Send service announcements, security notices, and policy updates</li>
            <li>Send transactional emails related to your account (password resets, subscription confirmations)</li>
          </ul>

          <h3>Platform Improvement</h3>
          <ul>
            <li>Analyze aggregate usage patterns to improve features and user experience</li>
            <li>Monitor and resolve technical issues and errors</li>
            <li>Develop new tools and functionality based on user behavior trends</li>
            <li>Conduct internal research and analytics (using aggregated, de-identified data)</li>
          </ul>

          <h3>Legal and Security</h3>
          <ul>
            <li>Detect, investigate, and prevent fraud, abuse, or unauthorized access</li>
            <li>Enforce our Terms of Service</li>
            <li>Comply with applicable legal obligations</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>4. Email and Alert Subscriptions</h2>
          <div className="legal-info-box">
            <h3>📧 You Control Your Email Preferences</h3>
            <p>You have full control over your email subscriptions at any time through your account profile:</p>
            <ul>
              <li>Enable or disable IV alert emails</li>
              <li>Enable or disable daily market report emails</li>
              <li>Enable or disable trade signal notifications</li>
              <li>Update your email address</li>
              <li>Unsubscribe from all non-essential emails</li>
            </ul>
          </div>
          <p>
            Even if you unsubscribe from marketing or alert emails, we may still send you transactional messages
            necessary to operate your account (e.g., password resets, security alerts, billing confirmations).
          </p>
        </div>

        <div className="legal-section">
          <h2>5. AI-Generated Content and Data Processing</h2>
          <p>
            Wealth Financial Systems uses artificial intelligence and machine learning tools to generate market
            commentary, signal analysis, sentiment summaries, and educational content. In connection with these
            features:
          </p>
          <ul>
            <li>AI-generated content is produced using publicly available market data and your in-session inputs</li>
            <li>We do not share your personal account information with external AI providers to generate content</li>
            <li>AI outputs are illustrative and educational — they do not constitute personalized financial advice</li>
            <li>You should independently verify any AI-generated analysis before making financial decisions</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>6. Information Sharing and Disclosure</h2>
          <p>
            <strong>We do not sell your personal information to third parties.</strong> We may share your
            information only in the following limited circumstances:
          </p>

          <h3>Service Providers</h3>
          <p>We share data with trusted vendors who help us operate the Service under confidentiality obligations:</p>
          <ul>
            <li><strong>Cloud Infrastructure:</strong> Amazon Web Services (AWS) — hosting, storage, email delivery (SES), authentication (Cognito)</li>
            <li><strong>Payment Processing:</strong> Third-party payment processors (e.g., Stripe) for subscription billing — they operate under their own privacy policies</li>
            <li><strong>Financial Data Providers:</strong> Third-party market data APIs (e.g., Financial Modeling Prep) to retrieve publicly available market data — your personal identity is not shared in these data requests</li>
            <li><strong>Analytics:</strong> Aggregate, non-personally-identifiable usage analytics</li>
          </ul>

          <h3>Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, reorganization, or sale of assets, your information may be
            transferred as part of that transaction. We will notify you via email or prominent notice on the Service
            before your information becomes subject to a different privacy policy.
          </p>

          <h3>Legal Requirements</h3>
          <p>We may disclose your information when we believe in good faith that disclosure is necessary to:</p>
          <ul>
            <li>Comply with a legal obligation, court order, or government request</li>
            <li>Protect and defend our legal rights or property</li>
            <li>Prevent or investigate fraud, security issues, or technical problems</li>
            <li>Protect the personal safety of users or the public</li>
          </ul>

          <h3>With Your Consent</h3>
          <p>We may share your information for any other purpose with your explicit consent.</p>
        </div>

        <div className="legal-section">
          <h2>7. Data Security</h2>
          <p>
            We implement industry-standard technical and organizational security measures to protect your personal
            information from unauthorized access, disclosure, alteration, or destruction:
          </p>
          <ul>
            <li><strong>Encryption in Transit:</strong> All data transmitted between your browser and our servers is encrypted using TLS/HTTPS</li>
            <li><strong>Encryption at Rest:</strong> Sensitive data stored in our databases is encrypted at rest</li>
            <li><strong>Access Controls:</strong> Personal information is accessible only to personnel who need it to operate and improve the Service</li>
            <li><strong>Authentication:</strong> Secure user authentication using JWT tokens with server-side password hashing</li>
            <li><strong>Infrastructure Security:</strong> AWS cloud infrastructure with enterprise-grade security controls, regular audits, and monitoring</li>
            <li><strong>Secrets Management:</strong> API keys and credentials are stored in AWS Secrets Manager, not in code</li>
          </ul>
          <p>
            No method of transmission over the Internet or method of electronic storage is 100% secure. While we
            strive to use commercially acceptable means to protect your information, we cannot guarantee absolute
            security. In the event of a data breach, we will notify affected users as required by applicable law.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Data Retention</h2>
          <p>We retain your personal information for as long as necessary to:</p>
          <ul>
            <li>Provide the Service and maintain your account</li>
            <li>Comply with applicable legal, tax, and accounting obligations</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Prevent fraud and abuse</li>
          </ul>
          <p>
            Upon account deletion, we will delete or anonymize your personal information within 30 days, except
            where retention is required by law (e.g., financial transaction records, which may be retained for up
            to 7 years per tax regulations). Market data, signals, and system logs that do not contain personal
            information may be retained indefinitely in anonymized form.
          </p>
        </div>

        <div className="legal-section">
          <h2>9. Cookies and Tracking Technologies</h2>
          <p>We use the following types of cookies and similar technologies:</p>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for authentication and core Service functionality. Cannot be disabled without disrupting the Service.</li>
            <li><strong>Preference Cookies:</strong> Store your display preferences and settings between sessions</li>
            <li><strong>Analytics Cookies:</strong> Help us understand aggregate usage patterns to improve the Service</li>
          </ul>
          <p>
            You can control cookies through your browser settings. Disabling essential cookies will prevent you from
            logging in and using the Service. We do not use cookies for advertising or cross-site tracking.
          </p>
        </div>

        <div className="legal-section">
          <h2>10. Your Privacy Rights</h2>
          <p>
            Depending on your location, you may have rights regarding your personal information. To exercise any
            of these rights, contact us at <strong>privacy@wealthfinancialsystems.com</strong>.
          </p>

          <h3>Access and Portability</h3>
          <ul>
            <li>Request a copy of the personal information we hold about you</li>
            <li>Request your data in a portable, machine-readable format</li>
          </ul>

          <h3>Correction</h3>
          <ul>
            <li>Request correction of inaccurate or incomplete personal information</li>
          </ul>

          <h3>Deletion</h3>
          <ul>
            <li>Request deletion of your personal information ("right to be forgotten"), subject to our retention obligations described in Section 8</li>
          </ul>

          <h3>Restriction and Objection</h3>
          <ul>
            <li>Object to or request restriction of certain processing activities</li>
            <li>Withdraw consent where processing is based on consent (this will not affect processing already performed)</li>
          </ul>

          <p>
            We will respond to verified requests within 30 days. We may need to verify your identity before
            processing your request.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. California Privacy Rights (CCPA / CPRA)</h2>
          <p>If you are a California resident, you have the following additional rights:</p>
          <ul>
            <li><strong>Right to Know:</strong> Request disclosure of what personal information we collect, use, disclose, and sell (we do not sell personal information)</li>
            <li><strong>Right to Delete:</strong> Request deletion of personal information we have collected about you, subject to certain exceptions</li>
            <li><strong>Right to Correct:</strong> Request correction of inaccurate personal information</li>
            <li><strong>Right to Opt-Out of Sale/Sharing:</strong> We do not sell or share your personal information for cross-context behavioral advertising</li>
            <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA/CPRA rights</li>
            <li><strong>Right to Limit Sensitive Personal Information:</strong> We collect minimal sensitive personal information and do not use it beyond what is necessary to provide the Service</li>
          </ul>
          <p>
            To submit a California privacy request, contact us at <strong>privacy@wealthfinancialsystems.com</strong> with
            the subject line "California Privacy Request."
          </p>
        </div>

        <div className="legal-section">
          <h2>12. International Data Transfers</h2>
          <p>
            Wealth Financial Systems is operated in the United States. If you access the Service from outside the
            United States, your information will be transferred to and processed in the United States, where data
            protection laws may differ from those in your country.
          </p>
          <p>
            By using the Service, you consent to this transfer. We take steps to ensure that any international
            transfers are subject to appropriate safeguards, including encryption and contractual data protection
            agreements with our service providers.
          </p>
        </div>

        <div className="legal-section">
          <h2>13. Children's Privacy</h2>
          <p>
            The Service is not intended for individuals under the age of 18. We do not knowingly collect personal
            information from minors. If you are a parent or guardian and believe your child has provided personal
            information to us, please contact us immediately at <strong>privacy@wealthfinancialsystems.com</strong>.
            We will take steps to delete such information promptly.
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Third-Party Links and Services</h2>
          <p>
            The Service may contain links to third-party websites, tools, or resources. We are not responsible for
            the privacy practices of those third parties, and this Privacy Policy does not apply to them. We
            encourage you to review the privacy policies of any third-party sites you visit.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices, the Service,
            or applicable laws. When we make material changes, we will:
          </p>
          <ul>
            <li>Post the updated Privacy Policy on this page with a new "Last updated" date</li>
            <li>Send you an email notification if you have an account with us</li>
            <li>Display a prominent notice within the Service for significant changes</li>
          </ul>
          <p>
            Your continued use of the Service after the effective date of the updated Privacy Policy constitutes
            your acceptance of the changes.
          </p>
        </div>

        <div className="legal-section">
          <h2>16. Contact Us</h2>
          <p>
            If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices,
            please contact us:
          </p>
          <div className="legal-contact">
            <p>
              <strong>Wealth Financial Systems</strong><br />
              <strong>Privacy Inquiries:</strong> privacy@wealthfinancialsystems.com<br />
              <strong>General Support:</strong> support@wealthfinancialsystems.com<br />
              <strong>Subject Line:</strong> Privacy Policy Inquiry
            </p>
          </div>
          <p>
            We will respond to all privacy-related inquiries within 30 days of receipt.
          </p>
        </div>

        <div className="legal-footer">
          <p>
            <strong>Your privacy matters to us.</strong><br />
            Wealth Financial Systems is committed to handling your personal information transparently,
            responsibly, and in accordance with applicable law.
          </p>
        </div>
      </div>
    </div>
  );
};
